import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    useRef,
    useContext,
} from "react";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "./components/ui/sheet";
import {
    Chat,
    Sidebar,
    ChatBubble,
    ChatWindow,
    StandardBolag,
} from "./components/chatboxes";
import { ChatContext } from "./index";

const Embedder = forwardRef((props, ref) => {
    const { appState, setAppState } = useContext(ChatContext);
    const containerRef = useRef(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [settings, setSettings] = useState({});
    const [initialMessage, setInitialMessage] = useState("");
    const [initialMessageIsSilent, setInitialMessageIsSilent] = useState(false);
    const [chatContext, setChatContext] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const chatRef = useRef(null);

    const handleInitialize = (initSettings) => {
        let elementsArray = [];
        if (initSettings.contextSelector) {
            const elements = document.querySelectorAll(
                initSettings.contextSelector
            );
            elementsArray = Array.from(elements).map((element) =>
                element.textContent.trim().replace(/\s+/g, " ")
            );
        }
        if (initSettings.context) {
            elementsArray.push(initSettings.context);
        }

        if (initSettings.initialMessage) {
            setInitialMessage(initSettings.initialMessage);
            setInitialMessageIsSilent(
                initSettings.initialMessageIsSilent || false
            );
        }

        setChatContext(elementsArray);
        setSettings(initSettings);
        setIsInitialized(true);
    };

    useImperativeHandle(
        ref,
        () => ({
            openChat: (message, addToMessageHistory = true) => {
                if (message) {
                    setInitialMessage(message);
                    setInitialMessageIsSilent(!addToMessageHistory);
                }
                setIsSheetOpen(true);
            },
            initialize: handleInitialize,
        }),
        []
    );

    useEffect(() => {
        if (appState && !isInitialized) {
            handleInitialize(appState);
        }
    }, [appState, isInitialized]);

    useEffect(() => {
        // Set the global reference to the Embedder component
        window.chattrickComponent = {
            openChat: (message, addToMessageHistory = true) => {
                if (message) {
                    setInitialMessage(message);
                    setInitialMessageIsSilent(!addToMessageHistory);
                }
                setIsSheetOpen(true);
            },
        };

        return () => {
            // Clean up the global reference when the component unmounts
        };
    }, []);


    const ChatWidget = () => (
        <Chat
            ref={chatRef}
            apiBaseUrl={settings?.apiBaseUrl}
            botId={settings?.botId}
            initialMessage={initialMessage}
            initialMessageIsSilent={initialMessageIsSilent}
            chatContext={chatContext}
            persistConnection={true}
            customDisclaimerText={settings?.customDisclaimerText}
            privacyPolicy={settings?.privacyPolicy}
        />
    );

    const AppWrapper = ({ children }) => (
        <div
            id="chattrick-app"
            className="chattrick-wrapper"
            style={{ width: "100%", height: "100%" }}
        >
            {children}
        </div>
    );

    console.log("Settings:", settings);

    if (!isInitialized) {
        return null;
    }

    if (settings?.renderMode === "standard-bolag") {
        return (
            <AppWrapper>
                <StandardBolag
                    apiBaseUrl={settings?.apiBaseUrl}
                    botId={settings?.botId}
                    customDisclaimerText={settings?.customDisclaimerText}
                    initialMessage={initialMessage}
                    initialMessageIsSilent={initialMessageIsSilent}
                    chatContext={chatContext}
                />
            </AppWrapper>
        );
    }

    if (settings?.renderMode === "chat-window") {
        return (
            <AppWrapper>
                <ChatWindow
                    apiBaseUrl={settings?.apiBaseUrl}
                    botId={settings?.botId}
                    initialMessage={initialMessage}
                    initialMessageIsSilent={initialMessageIsSilent}
                    chatContext={chatContext}
                    customDisclaimerText={settings?.customDisclaimerText}
                    settings={settings}
                />
            </AppWrapper>
        );
    }

    if (settings?.renderMode === "sidebar") {
        return (
            <AppWrapper>
                <Sidebar
                    title={settings?.styling?.title}
                    description={settings?.styling?.description}
                    isOpen={isSheetOpen}
                    onClose={() => setIsSheetOpen(false)}
                    side={settings.position}
                    settings={settings}
                >
                    <ChatWidget />
                </Sidebar>
            </AppWrapper>
        );
    }

    if (settings?.renderMode === "static") {
        return (
            <AppWrapper>
                <div className="flex flex-col w-full h-full space-y-4">
                    {(settings?.styling?.title ||
                        settings.styling?.description) && (
                        <div>
                            {settings.styling.title && (
                                <h2 className="text-lg font-semibold">
                                    {settings?.styling.title}
                                </h2>
                            )}
                            {settings.styling.description && (
                                <p className="text-xs sm:text-sm text-muted-foreground">
                                    {settings?.styling.description}
                                </p>
                            )}
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto">
                        <ChatWidget />
                    </div>
                </div>
            </AppWrapper>
        );
    }

    if (settings?.renderMode === "chat-bubble") {
        return (
            <AppWrapper>
                <ChatBubble
                    settings={settings}
                    apiBaseUrl={settings?.apiBaseUrl}
                    botId={settings?.botId}
                    initialMessage={initialMessage}
                    initialMessageIsSilent={initialMessageIsSilent}
                    chatContext={chatContext}
                    customDisclaimerText={settings?.customDisclaimerText}
                />
            </AppWrapper>
        );
    }

    return (
        <AppWrapper>
            <div ref={containerRef}>
                <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                    <SheetContent
                        container={containerRef.current}
                        side={settings?.side ?? "right"}
                        className="flex flex-col h-full p-0"
                    >
                        <SheetHeader className="px-4 pt-6">
                            <SheetTitle>
                                {settings?.title ?? "Chattrick"}
                            </SheetTitle>
                            <SheetDescription className="text-xs sm:text-sm">
                                {settings?.description ??
                                    "This is our chatbot Chattrick. You can ask him anything."}
                            </SheetDescription>
                        </SheetHeader>
                        <div className="px-4 pb-4 flex-1 overflow-hidden">
                            <ChatWidget />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </AppWrapper>
    );
});

export default Embedder;
